/**
 * Blog / News listing page: client-side category + search + pagination
 * engine, driving the category-chip row and the article grid - matches
 * the 4PhonesBlog Figma export. The intro hero (heading/subtitle/feature
 * badges) is static markup with no per-article data, so this script never
 * touches it.
 *
 * Combining articles from more than one Shopify blog and filtering /
 * paginating them together isn't something Shopify's native blog/tag
 * routing or {% paginate %} tag supports (they only ever operate on a
 * single blog). The section renders a fully working single-blog fallback
 * server-side for no-JS visitors and crawlers; this script takes over on
 * load to combine every configured blog and drive the interactive page.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-blog-page]');
  if (!root) return;

  var PER_PAGE = 9;
  var SEARCH_DEBOUNCE_MS = 400;

  var dynamicEl = root.querySelector('[data-blog-dynamic]');
  var chipsRowEl = root.querySelector('[data-blog-chips]');
  var resultsEl = root.querySelector('[data-blog-results]');
  var searchInput = root.querySelector('[data-blog-search-input]');
  var searchClearEl = root.querySelector('[data-blog-search-clear]');
  var searchSpinnerEl = root.querySelector('[data-blog-search-spinner]');
  var datasetEl = root.querySelector('[data-blog-dataset]');

  var strings = window.__blogPageStrings || {};

  var rawArticles = [];
  try {
    rawArticles = JSON.parse((datasetEl && datasetEl.textContent) || '[]');
  } catch (error) {
    rawArticles = [];
  }

  // Nothing to enhance (dataset missing/empty) - leave the server-rendered
  // fallback exactly as-is.
  if (!rawArticles.length || !dynamicEl) return;

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  function initials(name) {
    var words = String(name || '').trim().split(/\s+/).filter(Boolean);
    return words
      .slice(0, 2)
      .map(function (w) {
        return w.charAt(0);
      })
      .join('')
      .toUpperCase();
  }

  // Blog vs. Tags: Shopify's Blog object separates content into different
  // blogs (e.g. "News", "Guides", "Company Updates") - that's
  // typeLabel/typeHandle, and it drives both the card badge AND the topic
  // chips + chip filtering. Tags are a separate, finer-grained mechanism
  // that only powers the search box's text matching below - they never
  // appear as their own chip.
  var pool = rawArticles.map(function (a) {
    return {
      id: a.id,
      title: a.title || '',
      url: a.url,
      excerpt: a.excerpt || '',
      image: a.image,
      author: a.author || '',
      publishedAt: a.publishedAt,
      publishedLabel: a.publishedLabel || '',
      tags: a.tags || [],
      typeLabel: a.typeLabel || '',
      typeHandle: a.typeHandle || '',
    };
  });

  function readState() {
    var params = new URLSearchParams(window.location.search);
    return {
      cat: params.get('cat') || '',
      q: params.get('q') || '',
      page: Math.max(1, parseInt(params.get('page'), 10) || 1),
    };
  }

  var state = readState();

  function urlFor(page) {
    var params = new URLSearchParams();
    if (state.cat) params.set('cat', state.cat);
    if (state.q) params.set('q', state.q);
    if (page > 1) params.set('page', page);
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function writeState() {
    window.history.pushState(state, '', urlFor(state.page));
  }

  // Real, shareable href for a category chip (not used for navigation -
  // clicks are always intercepted below - just so the link is meaningful
  // on middle-click/copy-link/keyboard activation).
  function urlForCat(cat) {
    var params = new URLSearchParams();
    if (cat) params.set('cat', cat);
    if (state.q) params.set('q', state.q);
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  // One chip per distinct blog, in first-seen order (the dataset always
  // lists this section's own blog's articles first, then each additional
  // block's blog in the order it was added) - not alphabetical, so the
  // primary blog's chip always comes right after "All topics".
  function categories() {
    var seen = {};
    var out = [];
    pool.forEach(function (a) {
      if (a.typeHandle && !seen[a.typeHandle]) {
        seen[a.typeHandle] = true;
        out.push({ handle: a.typeHandle, label: a.typeLabel || a.typeHandle });
      }
    });
    return out;
  }

  function filtered() {
    var q = state.q.trim().toLowerCase();
    return pool.filter(function (a) {
      if (state.cat && a.typeHandle !== state.cat) return false;
      if (q && (a.title + ' ' + a.excerpt + ' ' + a.tags.join(' ') + ' ' + a.typeLabel).toLowerCase().indexOf(q) === -1) return false;
      return true;
    });
  }

  // ---------------------------------------------------------------------
  // Markup builders - mirror snippets/article-card.liquid and
  // snippets/blog-pagination.liquid exactly (same classes) so the
  // JS-rendered and server-rendered markup are visually identical.
  // ---------------------------------------------------------------------

  function cardHtml(a) {
    var imageHtml = a.image ? '<img src="' + a.image + '" loading="lazy" alt="">' : '';
    var catHtml = a.typeLabel ? '<span class="article-card_categorie">' + escapeHtml(a.typeLabel) + '</span>' : '';
    return (
      '<a href="' +
      a.url +
      '" class="article-card_comp u-overflow-hidden u-position-relative">' +
      '<div class="article-card_image">' +
      imageHtml +
      '</div>' +
      '<div class="article-card_footer">' +
      catHtml +
      '<h3 class="article-card_title">' +
      escapeHtml(a.title) +
      '</h3>' +
      '<p class="article-card_excerpt">' +
      escapeHtml(a.excerpt) +
      '</p>' +
      '<div class="article-card_spacer"></div>' +
      '<div class="article-card_divider"></div>' +
      '<div class="article-card_meta-row">' +
      '<div class="article-card_author">' +
      '<span class="article-card_avatar">' +
      escapeHtml('4P') +
      '</span>' +
      '<span class="article-card_author-text">' +
      '<span class="article-card_author-label">' +
      escapeHtml(strings.writtenBy || '') +
      '</span>' +
      '<span class="article-card_author-name">' +
      escapeHtml('4Phones Team') +
      '</span>' +
      '</span>' +
      '</div>' +
      '<div class="article-card_date">' +
      '<span class="article-card_date-label">' +
      escapeHtml(strings.postedOn || '') +
      '</span>' +
      '<span class="article-card_date-value">' +
      escapeHtml(a.publishedLabel) +
      '</span>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</a>'
    );
  }

  function chipsHtml() {
    var cats = categories();
    var html =
      '<a href="' +
      urlForCat('') +
      '" class="blog-chip' +
      (!state.cat ? ' is--active' : '') +
      '" data-blog-chip="">' +
      escapeHtml(strings.allTopics || '') +
      '</a>';
    cats.forEach(function (c) {
      html +=
        '<a href="' +
        urlForCat(c.handle) +
        '" class="blog-chip' +
        (state.cat === c.handle ? ' is--active' : '') +
        '" data-blog-chip="' +
        escapeHtml(c.handle) +
        '">' +
        escapeHtml(c.label) +
        '</a>';
    });
    return html;
  }

  function paginationHtml(totalItems, page) {
    var totalPages = Math.max(1, Math.ceil(totalItems / PER_PAGE));
    if (totalPages <= 1) return '';

    var parts = ['<nav aria-label="' + escapeHtml(strings.navLabel || '') + '" class="blog-pg_nav">'];

    if (page > 1) {
      parts.push(
        '<a href="' +
          urlFor(page - 1) +
          '" data-blog-page-link="' +
          (page - 1) +
          '" class="blog-pg_btn">' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          escapeHtml(strings.prev || '') +
          '</a>'
      );
    }

    var windowPages = buildPageWindow(totalPages, page);
    windowPages.forEach(function (n) {
      if (n === 'gap') {
        parts.push('<span class="blog-pg_page is--ellipsis">&hellip;</span>');
      } else if (n === page) {
        parts.push('<span class="blog-pg_page is--current" aria-current="page">' + n + '</span>');
      } else {
        parts.push('<a href="' + urlFor(n) + '" data-blog-page-link="' + n + '" class="blog-pg_page">' + n + '</a>');
      }
    });

    if (page < totalPages) {
      parts.push(
        '<a href="' +
          urlFor(page + 1) +
          '" data-blog-page-link="' +
          (page + 1) +
          '" class="blog-pg_btn">' +
          escapeHtml(strings.next || '') +
          '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</a>'
      );
    }

    parts.push('</nav>');
    return parts.join('');
  }

  function buildPageWindow(total, cur) {
    if (total <= 5) {
      var all = [];
      for (var i = 1; i <= total; i++) all.push(i);
      return all;
    }
    if (cur <= 3) return [1, 2, 3, 'gap', total];
    if (cur >= total - 2) return [1, 'gap', total - 2, total - 1, total];
    return [1, 'gap', cur, 'gap', total];
  }

  function renderChips() {
    if (chipsRowEl) chipsRowEl.innerHTML = chipsHtml();
  }

  function renderResults(shouldScroll) {
    var items = filtered().sort(function (a, b) {
      return b.publishedAt - a.publishedAt;
    });

    var totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE));
    if (state.page > totalPages) state.page = totalPages;

    var pageItems = items.slice((state.page - 1) * PER_PAGE, state.page * PER_PAGE);

    var html;
    if (!pageItems.length) {
      html = '<div class="blog_empty"><p>' + escapeHtml(strings.noResults || '') + '</p></div>';
    } else {
      var paging = paginationHtml(items.length, state.page);
      html =
        '<ul role="list" class="blog_grid w-list-unstyled">' +
        pageItems.map(function (a) { return '<li>' + cardHtml(a) + '</li>'; }).join('') +
        '</ul>' +
        (paging ? '<div class="blog-pg_wrap">' + paging + '</div>' : '');
    }

    if (resultsEl) resultsEl.innerHTML = html;

    if (shouldScroll) {
      var top = root.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }
  }

  function applyChange(shouldScroll) {
    state.page = 1;
    writeState();
    renderChips();
    renderResults(shouldScroll);
  }

  renderChips();
  renderResults(false);

  if (searchInput) {
    searchInput.value = state.q;
    if (searchClearEl) searchClearEl.hidden = !state.q;

    var searchTimer = null;
    searchInput.addEventListener('input', function () {
      var raw = searchInput.value;

      // Show the spinner immediately and hide the clear (x) button while
      // it's up, so the two never overlap in the same right-side slot -
      // results (and the clear button, if there's still a query) only
      // reappear once the debounce below actually fires.
      if (searchSpinnerEl) searchSpinnerEl.hidden = false;
      if (searchClearEl) searchClearEl.hidden = true;

      clearTimeout(searchTimer);
      searchTimer = setTimeout(function () {
        state.q = raw.trim();
        applyChange(false);
        if (searchSpinnerEl) searchSpinnerEl.hidden = true;
        if (searchClearEl) searchClearEl.hidden = !state.q;
      }, SEARCH_DEBOUNCE_MS);
    });
  }

  if (searchClearEl) {
    searchClearEl.addEventListener('click', function () {
      if (searchInput) {
        searchInput.value = '';
        searchInput.focus();
      }
      searchClearEl.hidden = true;
      state.q = '';
      applyChange(false);
    });
  }

  root.addEventListener('click', function (event) {
    // NOTE: these must be their own attributes (not the root's
    // [data-blog-page] marker) - closest() also matches the root section
    // itself as an ancestor of literally every click in the page, which
    // would call preventDefault() on every card image/title/button click
    // and silently block navigation to the article.
    var pageLink = event.target.closest('[data-blog-page-link]');
    if (pageLink) {
      event.preventDefault();
      state.page = parseInt(pageLink.getAttribute('data-blog-page-link'), 10) || 1;
      writeState();
      renderResults(true);
      return;
    }

    var chip = event.target.closest('[data-blog-chip]');
    if (chip) {
      event.preventDefault();
      var value = chip.getAttribute('data-blog-chip') || '';
      state.cat = state.cat === value ? '' : value;
      applyChange(false);
    }
  });

  window.addEventListener('popstate', function () {
    state = readState();
    if (searchInput) searchInput.value = state.q;
    if (searchClearEl) searchClearEl.hidden = !state.q;
    if (searchSpinnerEl) searchSpinnerEl.hidden = true;
    renderChips();
    renderResults(false);
  });
})();
